import { createAuthFlow, type SignInMethod } from "@aybuben/client/auth/flow.ts";
import type { AuthSource, AuthUser } from "@aybuben/client/auth/source.ts";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { useStore } from "zustand";

import { useStrings } from "../i18n/index.tsx";
import { COL } from "../theme.ts";
import { G } from "../ui/primitives.tsx";
import { FlowBody } from "./FlowBody.tsx";

interface AuthFlowPageProps {
	auth: AuthSource;
	onDone: (u: AuthUser, method: SignInMethod) => void;
	/** An address to start from, e.g. the one a guest just found taken. */
	initialEmail?: string;
}

export function AuthFlowPage({ auth, onDone, initialEmail }: AuthFlowPageProps) {
	const location = useLocation();
	const [params, setParams] = useSearchParams();
	const [flow] = useState(() => createAuthFlow({ auth, guest: false }));
	const step = useStore(flow, (s) => s.step);
	const urlStep = params.get("step");
	const shown = step.k === "code" ? step.k : null;

	// The code step is a history entry, so the phone's back gesture returns to
	// the email field instead of leaving sign-in.
	useEffect(() => {
		if (shown && urlStep !== shown) setParams({ step: shown });
	}, [shown]);
	useEffect(() => {
		if (urlStep === shown) return;
		// The URL moved away from the step on screen (back, forward, a reload).
		// A code step cannot be rebuilt from a URL, so start over.
		if (shown) flow.getState().changeEmail();
		if (urlStep) setParams({}, { replace: true });
	}, [urlStep]);
	useEffect(() => {
		if (step.k === "done") onDone(step.user, step.method);
	}, [step]);

	// Safe: Root navigates here with `{ email }` state or with none; anything
	// else reads as no email.
	const passed = (location.state as { email?: unknown } | null)?.email;
	return (
		<div className="px-6 pt-4 pb-16">
			<FlowBody
				flow={flow}
				guest={false}
				initialEmail={initialEmail || (typeof passed === "string" ? passed : "")}
				entryHeader={<EntryHeader />}
			/>
		</div>
	);
}

function EntryHeader() {
	const t = useStrings();
	return (
		<>
			<G size={30} style={{ color: COL.pom, fontWeight: 700 }}>
				Այբուբեն
			</G>
			<h1 className="mt-3 text-2xl font-extrabold">{t("auth.entryTitle")}</h1>
			<p className="mt-2 text-sm leading-relaxed" style={{ color: COL.mute }}>
				{t("auth.entryBody")}
			</p>
		</>
	);
}
